import cloneDeep from 'lodash/cloneDeep.js'
import set from 'lodash/set.js'
import {createSelector} from 'reselect'
import {NEW_ID} from '../../../common/constants/index.js'
import {SETTINGS, USERS} from '../../../common/constants/SettingsPanels.js'
import {setUser, userSelector} from '../../../data/users.js'
import {formsSelector, getState, setForm, updateForm} from '../../../store.js'
import {locationSelector} from '../../../redux/selectors/ui/location.js'
import {isPositiveNumeric, isPresent} from '../../../common/utils.js'
import {buildPath} from '../../../common/querystring.js'
import api from '../../../common/api.js'
import {navigate} from '../../../common/location.js'
import {showMessageToast} from '../../Header/Toast/index.js'

export const SETTINGS_USERS = 'settings_users'

const INITIAL_FORM = {
  name: '',
  email: '',
  password: '',
  passwordConfirm: '',
  permissions: {},
  isSaving: false,
  serverError: null,
}

export function setupUsersForm() {
  return {
    formName: SETTINGS_USERS,
    initialForm: INITIAL_FORM,
  }
}

export function setupForEdit() {
  const userID = settingsUserIDSelector(getState())
  const user = userSelector(getState(), {userID})

  if (!user) {
    return
  }

  setForm(SETTINGS_USERS, {
    ...INITIAL_FORM,
    name: user.name || '',
    email: user.email || '',
    permissions: cloneDeep(user.permissions),
  })
}

export function setupForAdding() {
  setForm(SETTINGS_USERS, {...INITIAL_FORM, id: NEW_ID})
}

export const settingsUserIDSelector = createSelector(
  locationSelector,
  ({pathComponents}) => {
    if (
      pathComponents[0] === SETTINGS &&
      pathComponents[1] === USERS &&
      isPositiveNumeric(pathComponents[2])
    ) {
      return Number(pathComponents[2])
    }

    return null
  },
)

export function resetUsersForm() {
  setForm(SETTINGS_USERS, INITIAL_FORM)
}

export function updateUsersForm(...args) {
  updateForm(SETTINGS_USERS, ...args)
}

export function updatePermissions(path, value) {
  const {permissions} = usersFormSelector(getState())

  set(permissions, path, value)

  updateUsersForm({permissions})
}

export function usersFormSelector(state) {
  return formsSelector(state)[SETTINGS_USERS] || usersFormSelector.default
}
usersFormSelector.default = {}

export const serverParamErrorsSelector = createSelector(
  usersFormSelector,
  ({serverError}) => {
    let errors = {}

    if (serverError?.param === 'name') {
      errors.name = serverError.error_message
    }

    if (serverError?.param === 'email') {
      errors.email = serverError.error_message
    }

    if (serverError?.param === 'password') {
      errors.password = serverError.error_message
    }

    if (serverError && Object.keys(errors).length === 0) {
      return {serverError: serverError.error_message || serverError.message}
    }

    return errors
  },
)

export const userSettingsErrorsSelector = createSelector(
  usersFormSelector,
  serverParamErrorsSelector,
  (form, serverParamErrors) => {
    const errors = {...serverParamErrors}

    if (!isPresent(form.name)) {
      errors.name = 'Name is required'
      errors.preventSave = true
    }

    if (!isPresent(form.email)) {
      errors.email = 'Email is required'
      errors.preventSave = true
    }

    if (form.isChangingPassword) {
      if (!isPresent(form.password)) {
        errors.password = 'Password is required'
        errors.preventSave = true
      }

      if (!isPresent(form.passwordConfirm)) {
        errors.passwordConfirm = 'Password Confirmation is required'
        errors.preventSave = true
      }

      if (form.password !== form.passwordConfirm) {
        errors.passwordConfirm = 'Passwords do not match'
        errors.preventSave = true
      }
    }

    return errors
  },
)

export const settingsUserAddSelector = createSelector(
  locationSelector,
  ({pathComponents}) => {
    return (
      pathComponents[0] === SETTINGS &&
      pathComponents[1] === USERS &&
      pathComponents[2] === NEW_ID
    )
  },
)

export async function saveUser() {
  const userID = settingsUserIDSelector(getState())
  const isNew = settingsUserAddSelector(getState())
  const {name, email, password, permissions} = usersFormSelector(getState())

  const user = userSelector(getState(), {userID})

  const params = {
    name,
  }

  if (!user || email !== user.email) {
    params.email = email
  }

  if (password) {
    params.password = password
  }

  if (user) {
    params.permissions = permissions
  }

  updateUsersForm({isSaving: true, serverError: null})

  try {
    const {json} = await (isNew
      ? api.post('/user/', params)
      : api.put(buildPath(['user', userID]), params))

    setUser(json)

    showMessageToast(isNew ? 'User created.' : 'User updated.')

    return navigate([SETTINGS, USERS])
  } catch (err) {
    updateUsersForm({serverError: err, isSaving: false})
  }
}
